import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Navbar } from "../components/Navbar";
import { toast } from "sonner";
import {
  GraduationCap,
  Mail,
  Phone,
  Building2,
  CheckCircle2,
  Search,
  Copy,
  Loader2,
  UserPlus,
  KeyRound,
  TrendingUp,
  Users,
  CalendarDays,
  Clock,
  Trash2,
  Send,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const API = `${window.location.origin}/api`;

export default function AdminAccademiaPage() {
  const [tab, setTab] = useState("in_attesa");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [search, setSearch] = useState("");

  // Form "Crea/Reset utente manualmente"
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    email: "",
    nome: "",
    cognome: "",
    centro_estetico: "",
    telefono: "",
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualResult, setManualResult] = useState(null);

  // Stats
  const [stats, setStats] = useState(null);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/accademia/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch {
      // silente
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchLeads = async (filter = tab) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/admin/accademia/leads${filter ? `?stato=${filter}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.items || []);
      }
    } catch (e) {
      toast.error("Errore nel caricamento dei lead");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(tab);
  }, [tab]);

  const handleApprove = async (lead) => {
    if (!window.confirm(`Approvare l'accesso all'Accademia per ${lead.email}?\nVerrà inviata un'email con il link "Imposta password".`)) return;
    setApprovingId(lead.lead_id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/accademia/approva/${lead.lead_id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.email_sent
          ? `Email inviata a ${lead.email}`
          : `Approvato. ATTENZIONE: email NON inviata. Copia il link e invialo manualmente.`);
        if (!data.email_sent && data.set_password_link) {
          await navigator.clipboard.writeText(data.set_password_link);
          toast("Link copiato negli appunti", { icon: "📋" });
        }
        fetchLeads(tab);
        fetchStats();
      } else {
        toast.error(data.detail || "Errore approvazione");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setApprovingId(null);
    }
  };

  const filtered = leads.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (l.email || "").toLowerCase().includes(q) ||
      (l.nome || "").toLowerCase().includes(q) ||
      (l.telefono || "").toLowerCase().includes(q) ||
      (l.azienda || "").toLowerCase().includes(q)
    );
  });

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualForm.email.trim()) {
      toast.error("Email obbligatoria");
      return;
    }
    if (!window.confirm(
      `Creare/resettare l'account per ${manualForm.email} e inviare un'email con le credenziali in chiaro?\n\nUtile per i clienti che non riescono a usare il link "Imposta password".`
    )) return;
    setManualSubmitting(true);
    setManualResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/accademia/send-credentials-manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(manualForm),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setManualResult(data);
        toast.success(
          data.email_sent
            ? `Credenziali inviate a ${data.email}`
            : `Utente ${data.action === "created" ? "creato" : "aggiornato"}. ATTENZIONE: email NON inviata, copia la password sotto.`
        );
        fetchLeads(tab);
      } else {
        toast.error(data.detail || "Errore durante la creazione/reset");
      }
    } catch (err) {
      toast.error("Errore di rete");
    } finally {
      setManualSubmitting(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiato`);
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const handleResendCredentials = async (lead) => {
    if (!window.confirm(
      `Inviare nuove credenziali a ${lead.email}?\n\nVerrà generata una nuova password e inviata via email. Il vecchio accesso non funzionerà più.`
    )) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/accademia/resend-credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: lead.email }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setManualResult(data);
        setManualOpen(true);
        toast.success(
          data.email_sent
            ? `Nuove credenziali inviate a ${data.email}`
            : `Password rigenerata. Copia dal box (email NON inviata).`
        );
        fetchLeads(tab);
      } else {
        toast.error(data.detail || "Errore invio credenziali");
      }
    } catch {
      toast.error("Errore di rete");
    }
  };

  const handleDelete = async (lead) => {
    const msg = `Sei sicuro di voler ELIMINARE definitivamente questo cliente?\n\n${lead.email}\n\nVerrà cancellato il lead, l'account utente e le viste registrate.\n\nQuesta operazione NON è reversibile.`;
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API}/admin/accademia/leads/${lead.lead_id}?delete_user=true`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(
          data.deleted_user
            ? `Lead e utente eliminati: ${data.email}`
            : `Lead eliminato: ${data.email}`
        );
        fetchLeads(tab);
        fetchStats();
      } else {
        toast.error(data.detail || "Errore eliminazione");
      }
    } catch {
      toast.error("Errore di rete");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white pt-16 lg:pt-0 lg:mr-64" data-testid="admin-accademia-page">
      <Navbar />
      <div className="bg-white border-b border-rose-100 py-6 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-4">
          <img
            src="/odr-assets/logo_odr_accademia.jpg"
            alt="Accademia ODR"
            className="h-16 w-auto object-contain"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#B87A8A]" />
              Accademia ODR · Approvazione accessi
            </h1>
            <p className="text-sm text-gray-500">Gestione clienti che richiedono accesso alla Biblioteca Corsi</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="accademia-stats">
            {[
              { label: "Oggi", value: stats.today, icon: Clock, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Questa settimana", value: stats.week, icon: CalendarDays, color: "text-sky-600", bg: "bg-sky-50" },
              { label: "Questo mese", value: stats.month, icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50" },
              { label: `Totale (in attesa: ${stats.in_attesa})`, value: stats.total, icon: Users, color: "text-rose-600", bg: "bg-rose-50" },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={`stat-${s.label}`}
                className={`${s.bg} rounded-2xl p-4 border border-white/60`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-gray-600">{s.label}</p>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Grafico ultimi 30 giorni */}
        {stats?.daily_series?.length > 0 && (
          <div className="bg-white border border-rose-100 rounded-2xl p-4" data-testid="accademia-chart">
            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#B87A8A]" />
              Iscrizioni ultimi 30 giorni
            </p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.daily_series} margin={{ top: 6, right: 12, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-isc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#B87A8A" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#B87A8A" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5e1e3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#7A4856" }}
                    tickFormatter={(d) => d.slice(5)}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: "#7A4856" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, fontSize: 12 }}
                    labelFormatter={(d) => `Giorno ${d}`}
                    formatter={(v) => [`${v} iscritti`, "Iscrizioni"]}
                  />
                  <Area type="monotone" dataKey="count" stroke="#B87A8A" strokeWidth={2} fill="url(#grad-isc)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: "in_attesa", label: "In attesa di approvazione" },
            { id: "accademia_approvato", label: "Approvati" },
            { id: "", label: "Tutti i lead" },
          ].map((t) => (
            <button
              key={t.id || "all"}
              onClick={() => setTab(t.id)}
              data-testid={`accademia-tab-${t.id || "all"}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                tab === t.id ? "bg-[#B87A8A] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-rose-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, telefono o azienda..."
            className="pl-9 rounded-full"
            data-testid="accademia-search-input"
          />
        </div>

        {/* Crea / Reset utente manualmente */}
        <div className="bg-white border border-rose-100 rounded-2xl">
          <button
            type="button"
            onClick={() => setManualOpen((v) => !v)}
            data-testid="manual-toggle-btn"
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-rose-50/50 rounded-2xl transition"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <UserPlus className="w-4 h-4 text-[#B87A8A]" />
              Crea / resetta un utente manualmente (invia credenziali in chiaro)
            </span>
            <span className="text-xs text-gray-400">{manualOpen ? "Chiudi" : "Apri"}</span>
          </button>
          {manualOpen && (
            <form onSubmit={handleManualSubmit} className="px-5 pb-5 space-y-3 border-t border-rose-100 pt-4">
              <p className="text-xs text-gray-500">
                Bypassa il flusso "Imposta password". Genera una password sicura e invia subito un'email con email + password
                al cliente. Funziona anche se l'utente esisteva già (resetta la password).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Email *</label>
                  <Input
                    type="email"
                    required
                    value={manualForm.email}
                    onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                    placeholder="cliente@esempio.it"
                    data-testid="manual-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Telefono</label>
                  <Input
                    value={manualForm.telefono}
                    onChange={(e) => setManualForm({ ...manualForm, telefono: e.target.value })}
                    placeholder="+39 ..."
                    data-testid="manual-tel-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Nome</label>
                  <Input
                    value={manualForm.nome}
                    onChange={(e) => setManualForm({ ...manualForm, nome: e.target.value })}
                    placeholder="Cinzia"
                    data-testid="manual-nome-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Cognome</label>
                  <Input
                    value={manualForm.cognome}
                    onChange={(e) => setManualForm({ ...manualForm, cognome: e.target.value })}
                    placeholder="Cantini"
                    data-testid="manual-cognome-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-gray-600">Centro estetico</label>
                  <Input
                    value={manualForm.centro_estetico}
                    onChange={(e) => setManualForm({ ...manualForm, centro_estetico: e.target.value })}
                    placeholder="Estetica ..."
                    data-testid="manual-centro-input"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <Button
                  type="submit"
                  disabled={manualSubmitting}
                  className="rounded-full bg-[#B87A8A] hover:bg-[#a06978]"
                  data-testid="manual-submit-btn"
                >
                  {manualSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />Crea / Reset e invia email</>}
                </Button>
              </div>
              {manualResult && (
                <div
                  data-testid="manual-result-box"
                  className="mt-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50/60 text-sm space-y-2"
                >
                  <p className="font-semibold text-emerald-800">
                    {manualResult.action === "created" ? "Utente creato" : "Password resettata"} · {manualResult.email}
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs">Password:</span>
                    <code className="px-2 py-1 bg-white rounded border border-emerald-200 font-mono text-emerald-900">
                      {manualResult.password}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(manualResult.password, "Password")}
                      className="text-xs text-[#B87A8A] hover:underline flex items-center gap-1"
                      data-testid="manual-copy-pwd-btn"
                    >
                      <Copy className="w-3 h-3" /> copia
                    </button>
                  </p>
                  <p className="text-xs text-gray-600">
                    Email inviata: {manualResult.email_sent ? "✅ sì" : "❌ no"}
                    {manualResult.email_error && (
                      <span className="text-red-600 ml-2">({manualResult.email_error})</span>
                    )}
                  </p>
                  {!manualResult.email_sent && (
                    <p className="text-xs text-amber-700">
                      Copia la password e inviala manualmente al cliente. Login: <a className="underline" href={manualResult.login_url}>{manualResult.login_url}</a>
                    </p>
                  )}
                </div>
              )}
            </form>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <Loader2 className="w-8 h-8 mx-auto animate-spin" />
            <p className="mt-2 text-sm">Caricamento...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-rose-100 rounded-2xl p-10 text-center text-gray-500">
            <GraduationCap className="w-12 h-12 mx-auto opacity-30 mb-2" />
            <p>Nessun lead in questa sezione</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((lead) => {
              const isApproved = lead.stato === "accademia_approvato";
              return (
                <div
                  key={lead.lead_id}
                  data-testid={`accademia-lead-${lead.lead_id}`}
                  className="bg-white rounded-2xl border border-rose-100 p-5 hover:shadow-md transition-all relative"
                >
                  {lead.regione && (
                    <span
                      data-testid={`lead-regione-${lead.lead_id}`}
                      title="Regione"
                      className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-gradient-to-r from-[#B87A8A] to-[#7A4856] text-white text-sm font-semibold shadow-sm tracking-wide"
                    >
                      {lead.regione}
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-800 truncate">{lead.nome || "—"}</h3>
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm text-[#B87A8A] hover:underline truncate">
                        <Mail className="w-3 h-3" /> {lead.email}
                      </a>
                      {lead.telefono && (
                        <p className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <Phone className="w-3 h-3" /> {lead.telefono}
                        </p>
                      )}
                      {lead.azienda && (
                        <p className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <Building2 className="w-3 h-3" /> {lead.azienda}
                        </p>
                      )}
                      {lead.citta && (
                        <p className="text-xs text-gray-500 mt-1">📍 {lead.citta}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          fonte: {lead.fonte || "—"}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            isApproved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {lead.stato || "nuovo"}
                        </span>
                      </div>
                    </div>
                    {isApproved ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <div
                          className="text-green-600 flex items-center gap-1 text-sm"
                          data-testid={`approved-badge-${lead.lead_id}`}
                          title="Approvato"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <button
                          onClick={() => handleResendCredentials(lead)}
                          className="ml-1 px-2.5 py-1.5 text-[#7A4856] hover:bg-rose-50 rounded-full text-xs font-medium transition border border-rose-200 flex items-center gap-1"
                          title="Rigenera password e invia email con credenziali"
                          data-testid={`resend-btn-${lead.lead_id}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Invia credenziali</span>
                        </button>
                        <button
                          onClick={() => handleDelete(lead)}
                          className="ml-1 p-1.5 text-red-500 hover:bg-red-50 rounded-full transition"
                          title="Elimina lead e utente"
                          data-testid={`delete-btn-${lead.lead_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(lead)}
                          disabled={approvingId === lead.lead_id}
                          className="rounded-full bg-[#B87A8A] hover:bg-[#a06978]"
                          data-testid={`approve-btn-${lead.lead_id}`}
                        >
                          {approvingId === lead.lead_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Approva</>
                          )}
                        </Button>
                        <button
                          onClick={() => handleDelete(lead)}
                          className="ml-1 p-1.5 text-red-500 hover:bg-red-50 rounded-full transition"
                          title="Elimina lead"
                          data-testid={`delete-btn-${lead.lead_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
